import { useEffect, useRef } from 'react';
import { ButtonColor, ButtonVariant, IconButton, IconButtonProps, Skeleton } from '../../../components';
import { ComponentSize } from '../../../types';

const DEFAULT_WIDTH = 240;
const NUM_SKELETONS = 3;
const SKELETON_SIZE = 40;

type ExplorerColumnProps = {
  children: React.ReactNode;
  isLoading?: boolean;
  width?: number;
  useActions?: () => (Omit<IconButtonProps, 'color'> & { destructive?: boolean })[];
};

/**
 * Generic column component that provides layout, loading states,
 * and action buttons for a single column in the explorer view.
 */
const ExplorerColumn = ({ children, isLoading, width = DEFAULT_WIDTH, useActions }: ExplorerColumnProps) => {
  const columnRef = useRef<HTMLDivElement>(null);

  const actions = useActions?.();

  // Scroll this column into view when it is first rendered.
  // This ensures the newly added column is visible to the user.
  useEffect(() => {
    if (columnRef.current) {
      columnRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' });
    }
  }, []);

  return (
    <div className="flex h-full flex-none flex-col border-r" style={{ width }} ref={columnRef}>
      <div className="flex h-8 w-full flex-none items-center justify-end gap-1 border-b p-2">
        {actions?.map((action, index) => (
          <IconButton
            key={index}
            size={ComponentSize.X_SMALL}
            color={action.destructive ? ButtonColor.DESTRUCTIVE : ButtonColor.NEUTRAL}
            variant={ButtonVariant.GHOST}
            {...action}
          />
        ))}
      </div>
      <div className="h-full overflow-y-auto">
        {children}
        {isLoading &&
          Array.from({ length: NUM_SKELETONS }, (_, index) => (
            <div className="flex w-60 select-none p-2" key={index}>
              <Skeleton size={SKELETON_SIZE} />
            </div>
          ))}
      </div>
    </div>
  );
};

export default ExplorerColumn;
